import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formsAPI } from "../services/api";
import { BiErrorAlt, BiCheckCircle, BiArrowBack, BiHome } from "react-icons/bi";
import "../styles/FormErrors.css";

const FormErrors = () => {
   const { formId } = useParams();
   const navigate = useNavigate();
   const [errorReports, setErrorReports] = useState([]);
   const [formInfo, setFormInfo] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [resolutionDescription, setResolutionDescription] = useState("");
   const [resolvingId, setResolvingId] = useState(null);

   useEffect(() => {
      fetchData();
   }, [formId]);

   const fetchData = async () => {
      try {
         setLoading(true);
         setError(null);

         // Fetch error reports
         const errorResponse = await formsAPI.getErrorReports(formId);
         console.log("API Response:", errorResponse); // Debug log

         if (errorResponse.success) {
            const reports = errorResponse.data.error_reports || [];
            const formSubmission = errorResponse.data.form_submission || null;

            console.log("Error Reports:", reports); // Debug log
            console.log("Form Submission:", formSubmission); // Debug log

            setErrorReports(reports);
            setFormInfo(formSubmission);
         }
      } catch (err) {
         console.error("Error fetching data:", err);
         setError("Failed to load error reports");
      } finally {
         setLoading(false);
      }
   };

   const handleResolve = async (reportId) => {
      if (!resolutionDescription.trim()) {
         alert("Please provide a resolution description");
         return;
      }

      try {
         setResolvingId(reportId);
         await formsAPI.resolveErrorReport(formId, reportId, resolutionDescription);
         setResolutionDescription("");
         await fetchData(); // Refresh data
      } catch (err) {
         console.error("Error resolving report:", err);
         alert("Failed to resolve error report");
      } finally {
         setResolvingId(null);
      }
   };

   if (loading) {
      return (
         <div
            style={{
               display: "flex",
               flexDirection: "column",
               alignItems: "center",
               justifyContent: "center",
               minHeight: "200px",
               gap: "16px",
            }}
         >
            <div
               style={{
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid var(--primary-color)",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  animation: "spin 1s linear infinite",
               }}
            ></div>
            <p>Loading error reports...</p>
         </div>
      );
   }

   if (error) {
      return (
         <div
            style={{
               display: "flex",
               flexDirection: "column",
               alignItems: "center",
               justifyContent: "center",
               minHeight: "200px",
               gap: "16px",
               color: "var(--danger-color)",
            }}
         >
            <BiErrorAlt size={24} />
            <p>{error}</p>
         </div>
      );
   }

   // Check if there are any unresolved error reports
   const hasUnresolvedReports = errorReports.some((report) => !report.is_resolved);

   return (
      <div
         style={{
            padding: "24px",
            maxWidth: "1200px",
            margin: "0 auto",
         }}
      >
         {/* Navigation Buttons */}
         <div
            style={{
               display: "flex",
               justifyContent: "space-between",
               alignItems: "center",
               marginBottom: "24px",
            }}
         >
            <div
               style={{
                  display: "flex",
                  gap: "16px",
               }}
            >
               <button
                  onClick={() => navigate("/forms")}
                  style={{
                     display: "flex",
                     alignItems: "center",
                     gap: "8px",
                     padding: "8px 16px",
                     background: "var(--primary-color)",
                     color: "white",
                     border: "none",
                     borderRadius: "4px",
                     cursor: "pointer",
                     fontWeight: "500",
                     transition: "background 0.2s",
                  }}
                  onMouseOver={(e) => {
                     e.target.style.background = "var(--primary-color-dark)";
                  }}
                  onMouseOut={(e) => {
                     e.target.style.background = "var(--primary-color)";
                  }}
               >
                  <BiArrowBack /> Back to Forms Dashboard
               </button>
               <button
                  onClick={() => navigate("/dashboard")}
                  style={{
                     display: "flex",
                     alignItems: "center",
                     gap: "8px",
                     padding: "8px 16px",
                     background: "var(--primary-color)",
                     color: "white",
                     border: "none",
                     borderRadius: "4px",
                     cursor: "pointer",
                     fontWeight: "500",
                     transition: "background 0.2s",
                  }}
                  onMouseOver={(e) => {
                     e.target.style.background = "var(--primary-color-dark)";
                  }}
                  onMouseOut={(e) => {
                     e.target.style.background = "var(--primary-color)";
                  }}
               >
                  <BiHome /> Global Dashboard
               </button>
            </div>
         </div>

         {/* Form Information */}
         <div
            style={{
               marginBottom: "32px",
            }}
         >
            <div
               style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "24px",
               }}
            >
               <div
                  style={{
                     flex: 1,
                  }}
               >
                  <div
                     style={{
                        background: "var(--background-light)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "8px",
                        padding: "16px",
                     }}
                  >
                     <div
                        style={{
                           marginBottom: "8px",
                        }}
                     >
                        <strong>Form ID:</strong> {formId}
                     </div>
                     <div
                        style={{
                           marginBottom: "8px",
                        }}
                     >
                        <strong>Form Type:</strong>{" "}
                        {formInfo?.form_type
                           ?.replace("_", " ")
                           .replace(/\b\w/g, (l) => l.toUpperCase())}
                     </div>
                     {formInfo?.conversation_id && (
                        <div
                           style={{
                              marginBottom: "8px",
                           }}
                        >
                           <strong>Conversation ID:</strong> {formInfo.conversation_id}
                        </div>
                     )}
                     <div
                        style={{
                           marginBottom: "0",
                        }}
                     >
                        <strong>Submitted:</strong>{" "}
                        {formInfo?.created_at
                           ? new Date(formInfo.created_at).toLocaleString()
                           : "N/A"}
                     </div>
                  </div>
               </div>
               {/* Only show Report New Error button if there are no unresolved reports */}
               {!hasUnresolvedReports && (
                  <button
                     onClick={() => navigate(`/forms/${formId}/report`)}
                     style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "12px 24px",
                        background: "var(--danger-color)",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "500",
                        fontSize: "1.1em",
                        transition: "background 0.2s",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                     }}
                     onMouseOver={(e) => {
                        e.target.style.background = "var(--danger-color-dark)";
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                     }}
                     onMouseOut={(e) => {
                        e.target.style.background = "var(--danger-color)";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                     }}
                     onMouseDown={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                     }}
                  >
                     <BiErrorAlt /> Report New Error
                  </button>
               )}
            </div>
         </div>

         {/* Error Reports List */}
         <div
            style={{
               background: "var(--background)",
               borderRadius: "8px",
               padding: "24px",
            }}
         >
            <h2>Error Reports ({errorReports.length})</h2>

            {errorReports.length === 0 ? (
               <div
                  style={{
                     textAlign: "center",
                     padding: "32px",
                     color: "var(--text-muted)",
                  }}
               >
                  <p>No error reports found for this form.</p>
               </div>
            ) : (
               <div
                  style={{
                     display: "grid",
                     gap: "24px",
                     marginTop: "24px",
                  }}
               >
                  {errorReports.map((report) => (
                     <div
                        key={report.id}
                        style={{
                           background: "white",
                           border: `1px solid ${
                              report.is_resolved ? "var(--success-color)" : "var(--border-color)"
                           }`,
                           borderRadius: "8px",
                           overflow: "hidden",
                        }}
                     >
                        <div
                           style={{
                              background: "var(--background-light)",
                              padding: "12px 16px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderBottom: "1px solid var(--border-color)",
                           }}
                        >
                           <div
                              style={{
                                 fontWeight: "600",
                              }}
                           >
                              Report #{report.id}
                           </div>
                           <div
                              style={{
                                 display: "flex",
                                 alignItems: "center",
                                 gap: "6px",
                                 padding: "4px 8px",
                                 borderRadius: "4px",
                                 fontSize: "0.9em",
                                 background: report.is_resolved
                                    ? "var(--success-color)"
                                    : "var(--warning-color)",
                                 color: "white",
                              }}
                           >
                              <BiCheckCircle />
                              {report.is_resolved ? "Resolved" : "Unresolved"}
                           </div>
                        </div>

                        <div
                           style={{
                              padding: "16px",
                           }}
                        >
                           <div className="error-report-content">
                              <h4>Reported Fields:</h4>
                              <ul className="error-fields-list">
                                 {report.report_data?.error_fields?.map((field, fieldIndex) => (
                                    <li key={fieldIndex}>{field}</li>
                                 ))}
                              </ul>

                              {report.description && (
                                 <div className="error-description">
                                    <h4>Description:</h4>
                                    <p>{report.description}</p>
                                 </div>
                              )}

                              {report.image_url && (
                                 <div className="error-screenshot">
                                    <h4>Screenshot:</h4>
                                    <img
                                       src={report.image_url}
                                       alt="Error Report Screenshot"
                                       style={{
                                          maxWidth: "100%",
                                          height: "auto",
                                          borderRadius: "8px",
                                       }}
                                    />
                                 </div>
                              )}

                              {report.resolution_description && report.is_resolved && (
                                 <div className="resolution-description">
                                    <h4>Resolution:</h4>
                                    <p>{report.resolution_description}</p>
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {/* Bottom Resolution Form */}
            {hasUnresolvedReports && (
               <div
                  style={{
                     marginTop: "32px",
                     padding: "24px",
                     background: "var(--background)",
                     borderRadius: "8px",
                     border: "1px solid var(--border-color)",
                  }}
               >
                  <h3>Resolve All Errors</h3>
                  <div
                     style={{
                        marginTop: "16px",
                     }}
                  >
                     <textarea
                        value={resolutionDescription}
                        onChange={(e) => setResolutionDescription(e.target.value)}
                        placeholder="Describe how these issues were resolved..."
                        rows={4}
                        style={{
                           width: "100%",
                           padding: "12px",
                           border: "1px solid var(--border-color)",
                           borderRadius: "4px",
                           marginBottom: "16px",
                           resize: "vertical",
                        }}
                     />
                     <button
                        onClick={() => handleResolve(errorReports.find((r) => !r.is_resolved)?.id)}
                        disabled={resolvingId !== null || !resolutionDescription.trim()}
                        style={{
                           display: "flex",
                           alignItems: "center",
                           gap: "8px",
                           padding: "12px 24px",
                           background: "var(--success-color)",
                           color: "white",
                           border: "none",
                           borderRadius: "4px",
                           cursor: "pointer",
                           fontWeight: "500",
                           fontSize: "1.1em",
                           opacity:
                              resolvingId !== null || !resolutionDescription.trim() ? "0.7" : "1",
                        }}
                     >
                        {resolvingId !== null ? (
                           <>
                              <span
                                 style={{
                                    border: "2px solid #f3f3f3",
                                    borderTop: "2px solid var(--primary-color)",
                                    borderRadius: "50%",
                                    width: "16px",
                                    height: "16px",
                                    borderWidth: "2px",
                                    animation: "spin 1s linear infinite",
                                 }}
                              ></span>
                              Resolving...
                           </>
                        ) : (
                           <>
                              <BiCheckCircle />
                              Mark All as Resolved
                           </>
                        )}
                     </button>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};

export default FormErrors;
